import { PropertyType } from '@/api/enumerations';
import { AvmFactorsData, AvmInferenceData } from '@/api/workOrders/types';
import {
  FlexBox,
  OSNumber,
  TitleBox,
} from '@/components/Sections/Title/styles';
import { BoxContainer } from '@/components/UI/Box';
import { BackButton } from '@/components/UI/Button';
import { GridContainer } from '@/components/UI/Grid';
import { IconArrowCircleLeftMS, IconInventoryMS } from '@/constants/icons';
import { Box, Breadcrumbs, LinearProgress, Typography } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';

import {
  SectionIndexAutocomplete,
  SectionIndexItem,
  scrollToId,
} from './components/SectionIndexAutocomplete';
import { FacTabKey, FactorsScientificTreatment } from './Factors';
import useScientificTreatment from './hooks/useScientificTreatment';
import { InferenceScientificTreatment } from './Inference';
import { InfTabKey } from './Inference/types';
import { factorsSectionIndex, inferenceSectionIndex } from './sectionIndex';
import {
  BreadcrumbsBase,
  BreadcrumbsTitle,
  StyledMobileStepper,
} from './styles';

export function ScientificTreatmentSteps(): JSX.Element {
  const [facActiveTab, setFacActiveTab] = useState<FacTabKey>('homogenization');
  const [infActiveTab, setInfActiveTab] = useState<InfTabKey>('result');
  const [scrollTarget, setScrollTarget] = useState<string | null>(null);

  const {
    referenceNumber,
    avmData,
    isFactors,
    propertyType,
    completionData,
    graphs,
    loadingPage,
    navigateBack,
  } = useScientificTreatment();

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout>;
    if (scrollTarget) {
      timeout = setTimeout(() => {
        scrollToId(scrollTarget);
        setScrollTarget(null);
      }, 150);
    }
    return () => clearTimeout(timeout);
  }, [scrollTarget]);

  const handleSearchNavigate = useCallback(
    (item: SectionIndexItem): void => {
      if (isFactors) {
        if (item.tabKey === facActiveTab) {
          scrollToId(item.sectionId);
          return;
        }
        setFacActiveTab(item.tabKey as FacTabKey);
        setScrollTarget(item.sectionId);
      } else {
        if (item.tabKey === infActiveTab) {
          scrollToId(item.sectionId);
          return;
        }
        setInfActiveTab(item.tabKey as InfTabKey);
        setScrollTarget(item.sectionId);
      }
    },
    [isFactors, facActiveTab, infActiveTab]
  );

  if (loadingPage)
    return (
      <Box>
        <LinearProgress sx={{ m: '60px 60px 60px 160px' }} />
      </Box>
    );

  const sectionKeys = Object.keys(completionData?.sections ?? {});
  const totalSections = sectionKeys.length;
  const completeSections = Object.values(completionData?.sections ?? {}).filter(
    (s) => s.status === 'complete'
  ).length;

  const renderScientificTreatment = (
    propertyType: PropertyType
  ): JSX.Element => {
    if (isFactors) {
      return (
        <FactorsScientificTreatment
          avmData={avmData as AvmFactorsData}
          propertyType={propertyType}
          graphs={graphs}
          sectionsStatus={completionData?.sections}
          activeTab={facActiveTab}
          onTabChange={setFacActiveTab}
        />
      );
    }
    return (
      <InferenceScientificTreatment
        avmData={avmData as AvmInferenceData}
        graphs={graphs}
        sectionsStatus={completionData?.sections}
        activeTab={infActiveTab}
        onTabChange={setInfActiveTab}
      />
    );
  };

  return (
    <GridContainer>
      <BackButton onClick={navigateBack}>{IconArrowCircleLeftMS}</BackButton>
      <BoxContainer>
        <Box>
          <TitleBox>
            <Breadcrumbs
              separator="›"
              aria-label="breadcrumb"
              sx={{ '& .MuiBreadcrumbs-separator': { fontSize: '28px' } }}
            >
              <BreadcrumbsBase>Elaboração de Laudo</BreadcrumbsBase>
              <BreadcrumbsTitle>Tratamento Científico</BreadcrumbsTitle>
            </Breadcrumbs>
            <FlexBox alignItems="center">
              {referenceNumber && (
                <OSNumber sx={{ height: '32px' }}>
                  {IconInventoryMS}
                  número OS {referenceNumber}
                </OSNumber>
              )}
            </FlexBox>
          </TitleBox>
        </Box>
        <Box sx={{ width: '100%', mt: 2, mb: 1 }}>
          <StyledMobileStepper
            variant="progress"
            steps={101}
            position="static"
            activeStep={Math.round(completionData?.percentage ?? 0)}
            nextButton={<Box />}
            backButton={<Box />}
          />
          <Box display="flex" justifyContent="space-between" mt={0.5}>
            <Box display="flex">
              <Typography variant="caption" color="text.secondary">
                {completeSections} de {totalSections} seções completas
              </Typography>
              <Typography mx={1}>&middot;</Typography>
              <Typography variant="caption" color="text.secondary">
                {completionData?.required_filled ?? 0}/
                {completionData?.required_total ?? 0} campos obrigatórios
              </Typography>
            </Box>
            <Typography variant="body2" fontWeight={600} color="primary">
              {completionData?.percentage}%
            </Typography>
          </Box>
        </Box>
        <Box mt="32px" display="flex" justifyContent="flex-end">
          <SectionIndexAutocomplete
            options={isFactors ? factorsSectionIndex : inferenceSectionIndex}
            onNavigate={handleSearchNavigate}
          />
        </Box>
        <Box mt="8px">
          {avmData && propertyType && renderScientificTreatment(propertyType)}
        </Box>
      </BoxContainer>
    </GridContainer>
  );
}
